import { useRef, useCallback } from 'react';

/**
 * Hook for pushing real-time field updates to the preview iframe
 * via postMessage, avoiding full rebuilds on every keystroke.
 *
 * Maintains a message queue so that when the iframe reloads (page
 * navigation, cacheBust change), all pending changes are replayed
 * automatically after the new page loads.
 */
export function useRealtimePreview() {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // Accumulated messages to replay after iframe reload
  const messageQueue = useRef<Array<Record<string, unknown>>>([]);

  /** Post a message to the iframe and add it to the replay queue */
  const postAndQueue = useCallback((msg: Record<string, unknown>) => {
    // Deduplicate: for field/media updates, keep only the latest per cmsType+field
    const msgType = msg.type as string;
    if (msgType === 'cms:updateField') {
      messageQueue.current = messageQueue.current.filter(
        (m) => !(m.type === 'cms:updateField' && m.cmsType === msg.cmsType && m.field === msg.field)
      );
    } else if (msgType === 'cms:updateMedia') {
      messageQueue.current = messageQueue.current.filter(
        (m) => !(m.type === 'cms:updateMedia' && m.cmsType === msg.cmsType && m.mediaField === msg.mediaField)
      );
    } else if (msgType === 'cms:updateDesignVars') {
      // Only keep the latest design vars message
      messageQueue.current = messageQueue.current.filter((m) => m.type !== 'cms:updateDesignVars');
    } else if (msgType === 'cms:toggleSection') {
      messageQueue.current = messageQueue.current.filter(
        (m) => !(m.type === 'cms:toggleSection' && m.section === msg.section)
      );
    }
    messageQueue.current.push(msg);

    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(msg, '*');
    }
  }, []);

  /** Replay all queued messages into the iframe (called after iframe load) */
  const replayQueue = useCallback(() => {
    if (!iframeRef.current?.contentWindow) return;
    const win = iframeRef.current.contentWindow;
    for (const msg of messageQueue.current) {
      win.postMessage(msg, '*');
    }
  }, []);

  /** Clear the message queue (e.g. after a fresh preview build) */
  const clearQueue = useCallback(() => {
    messageQueue.current = [];
  }, []);

  const sendUpdate = useCallback((cmsType: string, field: string, value: unknown, richtext?: boolean) => {
    postAndQueue({ type: 'cms:updateField', cmsType, field, value, richtext });
  }, [postAndQueue]);

  const sendMediaUpdate = useCallback((cmsType: string, mediaField: string, url: string) => {
    postAndQueue({ type: 'cms:updateMedia', cmsType, mediaField, url });
  }, [postAndQueue]);

  const injectDesignVariables = useCallback((designVars: Record<string, unknown>) => {
    postAndQueue({ type: 'cms:updateDesignVars', variables: designVars });
  }, [postAndQueue]);

  const scrollToSection = useCallback((section: string) => {
    // Scroll commands don't need to be replayed
    if (!iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(
      { type: 'cms:scrollToSection', section },
      '*'
    );
  }, []);

  const scrollToField = useCallback((cmsType: string, field?: string, cmsId?: string) => {
    // Scroll commands don't need to be replayed
    if (!iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(
      { type: 'cms:scrollToField', cmsType, field, cmsId },
      '*'
    );
  }, []);

  const toggleSection = useCallback((section: string, visible: boolean) => {
    postAndQueue({ type: 'cms:toggleSection', section, visible });
  }, [postAndQueue]);

  const setEditMode = useCallback((enabled: boolean) => {
    if (!iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage({ type: 'cms:setEditMode', enabled }, '*');
  }, []);

  const highlightField = useCallback((cmsType: string | null, field?: string, cmsId?: string) => {
    if (!iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(
      { type: 'cms:highlightField', cmsType, field, cmsId },
      '*'
    );
  }, []);

  const highlightBySelector = useCallback((selector: string | null) => {
    if (!iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(
      { type: 'cms:highlightBySelector', selector },
      '*'
    );
  }, []);

  return {
    iframeRef,
    sendUpdate,
    sendMediaUpdate,
    injectDesignVariables,
    scrollToSection,
    scrollToField,
    toggleSection,
    setEditMode,
    highlightField,
    highlightBySelector,
    replayQueue,
    clearQueue,
  };
}
